import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Badge,
  Tooltip,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import T from 'T'
import BillingServiceNotificationAvatar from 'components/common/avatar/BillingServiceNotificationAvatar'

const ContactList = ({ contacts, selectedContactIds, onContactToggle, isLoading }) => {
  const [searchValue, setSearchValue] = useState('')

  const filteredContacts = useMemo(() => {
    if (!searchValue.trim()) return contacts

    const search = searchValue.toLowerCase()
    return contacts.filter(contact => {
      const fullName = `${contact.firstName} ${contact.lastName}`.toLowerCase()
      const email = contact.email.toLowerCase()
      return fullName.includes(search) || email.includes(search)
    })
  }, [contacts, searchValue])

  const isContactSelected = contactId => selectedContactIds.includes(contactId)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 240px)',
        overflowY: 'auto',
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <PersonOutlineIcon fontSize="small" />
          <Typography variant="subtitle2">{T.ACCOUNT_CONTACTS}</Typography>
        </Stack>

        <TextField
          fullWidth
          placeholder={T.SEARCH_PLACEHOLDER}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          variant="standard"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {!isLoading && filteredContacts.length === 0 && (
          <Box p={3} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              {searchValue ? T.NO_CONTACTS_FOUND : T.NO_CONTACTS_WITH_EMAIL}
            </Typography>
          </Box>
        )}

        {!isLoading && (
          <List>
            {filteredContacts.map(contact => {
              const isSelected = isContactSelected(contact.id)

              return (
                <ListItem
                  key={contact.id}
                  button
                  onClick={() => onContactToggle(contact)}
                  sx={{
                    '&:hover': { bgcolor: 'action.hover' },
                    ...(isSelected && { bgcolor: 'action.selected' }),
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox edge="start" checked={isSelected} tabIndex={-1} disableRipple size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${contact.firstName} ${contact.lastName}`}
                    secondary={contact.email}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                  />
                  <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                    {contact.locationCount > 0 && (
                      <Tooltip
                        arrow
                        title={T.ASSOCIATED_WITH_LOCATIONS.replace('{count}', contact.locationCount).replace(
                          '{plural}',
                          contact.locationCount > 1 ? 's' : ''
                        )}
                      >
                        <Badge badgeContent={contact.locationCount} color="primary">
                          <LocationOnIcon fontSize="small" color="action" />
                        </Badge>
                      </Tooltip>
                    )}
                    <Stack direction="row" spacing={0.5}>
                      {contact.billingNotifications && (
                        <Tooltip arrow title={T.BILLING_NOTIFICATIONS}>
                          <span>
                            <BillingServiceNotificationAvatar type="Billing" isActive={contact.billingNotifications} />
                          </span>
                        </Tooltip>
                      )}
                      {contact.serviceNotifications && (
                        <Tooltip arrow title={T.SERVICE_NOTIFICATIONS}>
                          <span>
                            <BillingServiceNotificationAvatar type="Service" isActive={contact.serviceNotifications} />
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                </ListItem>
              )
            })}
          </List>
        )}
      </Box>
    </Box>
  )
}

ContactList.propTypes = {
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string.isRequired,
      billingNotifications: PropTypes.bool,
      serviceNotifications: PropTypes.bool,
      locationCount: PropTypes.number,
    })
  ).isRequired,
  selectedContactIds: PropTypes.array.isRequired,
  onContactToggle: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
}

export default ContactList
